import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, OnChanges } from '@angular/core';
import { DataService } from '@app-core/services/data/data.service';
import { EXCLUDED_EVENTS, VIDEO_TYPE_LIST } from '@app-core/constants/constants';
import { AccessService } from '@app-core/services/access/access.service';
import { EVENTS_CONFIG } from '@app-core/constants/constants';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AssetConfigurationService } from '@app-asset-config/services/asset-configuration.service';
import { FormControl, FormGroup } from '@angular/forms';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { configEvents } from '@app-asset-config/models/tagging.model';
import { StorageService } from '@app-core/services/storage/storage.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { takeUntil } from 'rxjs/operators';
import { getClientConfig } from '@config/client-config';
@Component({
  selector: 'app-coaching-threshold',
  templateUrl: './coaching-threshold.component.html',
  styleUrls: ['./coaching-threshold.component.scss'],
})
export class CoachingThresholdComponent implements OnInit, OnChanges, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public fleetId: string;
  public loginName: string;
  public eventsConfig: configEvents[];
  public othersConfig: configEvents[];
  public customEvents: configEvents[];
  public driverFacingEvents: configEvents[];
  public coachingThersholdDirty: any;
  public isEventsTypeValid: boolean = true;
  public isCoachingThresholdFormValid: boolean = true;
  public currentMetricUnit: string;
  private unsubscribeOnDestroy: Subject<void> = new Subject<void>();
  public incidentsPerUnitLabel = '';
  public incidentsPerUnitDesc = '';
  private previousFormValue: {
    incidentsPer100Miles: number;
    coachingInterval: number;
    minimumEventThreshold: number;
  };

  @Input()
  public coachConfigLoader: boolean = true;
  @Input()
  public eventTypes: any = [];
  @Input()
  public incidentsPer100Miles: number = 10;
  @Input()
  public coachingInterval: number = 14;
  @Input()
  public minimumEventThreshold: number = 10;
  @Input()
  public loader: boolean = false;
  @Input()
  public enableSelfCoaching: boolean = false;

  @Output()
  public coachingThresholdValues = new EventEmitter<any>();

  public thresholdForm = new FormGroup({
    incidentsPer100Miles: new FormControl(null),
    coachingInterval: new FormControl(null),
    minimumEventThreshold: new FormControl(null),
  });

  constructor(
    public dataService: DataService,
    private accessService: AccessService,
    public translate: TranslateService,
    private gtmService: GoogleTagManagerService,
    private assetService: AssetConfigurationService,
    private storageService: StorageService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit() {
    ({ loginName: this.loginName } = this.accessService.getLoginInfo());

    const combinedEventsList = this.dataService.modifyFleeEvents();
    const modifiedEventsConfig = this.dataService.transformObject(combinedEventsList);

    const filteredCustomData = {};
    const filterStandardEvents = {};
    for (const key in modifiedEventsConfig) {
      if (!(key in EVENTS_CONFIG)) {
        filteredCustomData[key] = modifiedEventsConfig[key];
      } else {
        filterStandardEvents[key] = modifiedEventsConfig[key];
      }
    }

    const otherConfigList = [
      ...VIDEO_TYPE_LIST,
      {
        label: getClientConfig().configuration.externalEventsLabel,
        value: 'externalEvents',
      },
    ].filter((obj) => obj.value === 'externalEvents');

    this.othersConfig = this.processConfig(otherConfigList).filter((event) => !EXCLUDED_EVENTS.includes(event.value));
    this.eventsConfig = this.processConfig(filterStandardEvents).filter((event) => !EXCLUDED_EVENTS.includes(event.key));
    this.customEvents = this.processConfig(filteredCustomData).filter(
      (event) => event.key !== 'PotentialCrash' && event.label !== 'High-G Event' && !EXCLUDED_EVENTS.includes(event.key)
    );
    this.driverFacingEvents = this.eventsConfig
      .filter((event) => event.isDriverFacing)
      .filter((event) => !EXCLUDED_EVENTS.includes(event.key));

    this.updateData();
    this.thresholdForm.valueChanges.subscribe(() => {
      this.formValueChanges();
    });
    this.dataService._currentMetricUnit.pipe(takeUntil(this.unsubscribeOnDestroy)).subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
        this.updateIncidentsTexts();
      }
    });
  }

  public ngOnChanges(): void {
    this.updateData();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.unsubscribeOnDestroy.next();
    this.unsubscribeOnDestroy.complete();
  }

  public updateData() {
    this.thresholdForm.setValue(
      {
        incidentsPer100Miles: this.incidentsPer100Miles,
        coachingInterval: this.coachingInterval,
        minimumEventThreshold: this.minimumEventThreshold,
      },
      { emitEvent: false }
    );

    this.previousFormValue = {
      incidentsPer100Miles: this.incidentsPer100Miles,
      coachingInterval: this.coachingInterval,
      minimumEventThreshold: this.minimumEventThreshold,
    };
    this.updateEvents(this.eventTypes);
    this.subscribeForCoachingThershold();

    this.isEventsTypeValid = true;
  }

  /**
   * Stop Propogation incase of multiple selection to avoid closing of the menu
   * @param e Click event*/

  public selectEvent(e: Event) {
    console.log(e);
  }

  /**
   * Alter selcted event list array based on mat checkbox checked property
   * @param e Mat check box event
   * @param event Event type
   */
  public checkEvent(e: MatCheckboxChange, event: string) {
    this.updateCheckedStatus(this.eventsConfig, event, e.checked);
    this.updateCheckedStatus(this.othersConfig, event, e.checked);
    this.updateCheckedStatus(this.customEvents, event, e.checked);
    this.trackEventCheckboxMixpanel(event, e.checked);
    this.checkIfDifferenceExists();
  }

  private trackEventCheckboxMixpanel(eventKey: string, checked: boolean) {
    let category = '';
    let matchedEvent: configEvents | undefined;

    if (this.driverFacingEvents?.some((e) => e.key === eventKey)) {
      matchedEvent = this.driverFacingEvents.find((e) => e.key === eventKey);
      category = 'driver-facing-events';
    } else if (this.eventsConfig?.some((e) => e.key === eventKey)) {
      matchedEvent = this.eventsConfig.find((e) => e.key === eventKey);
      category = 'road-facing-events';
    } else if (this.customEvents?.some((e) => e.key === eventKey)) {
      matchedEvent = this.customEvents.find((e) => e.key === eventKey);
      category = 'custom-events';
    } else if (this.othersConfig?.some((e) => e.key === eventKey)) {
      matchedEvent = this.othersConfig.find((e) => e.key === eventKey);
      category = 'other-events';
    }

    if (!matchedEvent || !category) {
      return;
    }

    const eventName = matchedEvent.label?.toLowerCase().replace(/\s+/g, '-');
    this.mixpanelService.track(`checkbox-toggled/configurations-page/coaching-page/${category}/${eventName}`, {
      EventKey: eventKey,
      Checked: checked,
    });
  }

  public subscribeForCoachingThershold() {
    const key = 'coachingConfig';
    const coachingThersholdValues = this.storageService.getStorageValue(key);
    this.coachingThersholdDirty = coachingThersholdValues;
  }

  private checkIfDifferenceExists() {
    // checking event types difference
    const configArray = [...this.eventsConfig, ...this.othersConfig, ...this.customEvents];
    const eventTypes = this.coachingThersholdDirty?.eventTypes;
    let isEventTypeDifferenceExists = false;
    configArray?.map((val: any) => {
      let key = val.key;
      if (key === '0') {
        key = 'panic-button';
      }
      if ((!eventTypes?.includes(key) && val.checked) || (eventTypes?.includes(key) && !val.checked)) {
        isEventTypeDifferenceExists = true;
      }
    });
    // checking threshold form difference
    let isThresholdFormDifferenceExists = false;
    for (let key in this.thresholdForm.value) {
      if (this.coachingThersholdDirty[key] !== this.thresholdForm.value[key]) {
        isThresholdFormDifferenceExists = true;
      }
    }
    if (isEventTypeDifferenceExists || isThresholdFormDifferenceExists) {
      this.isEventsTypeValid = false;
    } else {
      this.isEventsTypeValid = true;
    }
  }

  private formValueChanges() {
    if (this.thresholdForm && Object.keys(this.coachingThersholdDirty).length) {
      const currentValue = this.thresholdForm.value;
      const prevValue = this.previousFormValue;

      if (currentValue.incidentsPer100Miles !== prevValue.incidentsPer100Miles) {
        this.mixpanelService.track('input-changed/configurations-page/coaching-page/settings/events-per-100-miles', {
          IncidentsPer100Miles: currentValue.incidentsPer100Miles,
        });
      }

      if (currentValue.coachingInterval !== prevValue.coachingInterval) {
        this.mixpanelService.track('input-changed/configurations-page/coaching-page/settings/duration-minutes', {
          CoachingInterval: currentValue.coachingInterval,
        });
      }

      if (currentValue.minimumEventThreshold !== prevValue.minimumEventThreshold) {
        this.mixpanelService.track('input-changed/configurations-page/coaching-page/settings/max-events-per-driver', {
          MinimumEventThreshold: currentValue.minimumEventThreshold,
        });
      }

      this.previousFormValue = {
        incidentsPer100Miles: currentValue.incidentsPer100Miles,
        coachingInterval: currentValue.coachingInterval,
        minimumEventThreshold: currentValue.minimumEventThreshold,
      };

      this.checkIfDifferenceExists();
    }
  }

  public onSubmit() {
    this.gtmService.coachingThresholdSubmit();
    this.loader = true;
    let selectedEvents = [
      ...this.assetService.processSelectedEvents(this.eventsConfig),
      ...this.assetService.processSelectedEvents(this.othersConfig),
      ...this.assetService.processSelectedEvents(this.customEvents),
    ];
    this.mixpanelService.track('button-clicked/configurations-page/coaching-page/settings/submit', {
      'Change Values': this.thresholdForm.value,
    });
    const body = {
      ...this.thresholdForm.value,
      eventTypes: selectedEvents,
    };

    this.coachingThresholdValues.emit(body);
  }

  /**
   * TODO: This clear check is a hack to reset all the states for working properly.
   * This should be fixed by refactoring the component with immutable states.
   * Currently this make the check boxes to flicker after saving.
   */

  private clearChecks(configArray: configEvents[]) {
    if (!configArray) return;
    configArray.forEach((element) => {
      element.checked = false;
    });
  }

  private clearAllCheckedStates() {
    this.clearChecks(this.eventsConfig);
    this.clearChecks(this.othersConfig);
    this.clearChecks(this.customEvents);
  }

  private updateEvents(selectedEvent: string[]) {
    this.clearAllCheckedStates();

    this.updateCheckedStatusInArray(this.eventsConfig, selectedEvent);
    this.updateCheckedStatusInArray(this.othersConfig, selectedEvent, true);
    this.updateCheckedStatusInArray(this.customEvents, selectedEvent);
  }

  private processConfig(configObject): configEvents[] {
    return Object.entries(configObject)
      .map(([key, value]: [string, configEvents]) => ({
        key,
        checked: false,
        ...value,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }

  private updateCheckedStatus(configArray: configEvents[], key: string, checked: boolean) {
    configArray.forEach((element: configEvents) => {
      if (key === element.key) {
        element.checked = checked;
      }
    });
  }

  private updateCheckedStatusInArray(configArray: configEvents[], selectedEvent: string[], isOtherEvents?: boolean) {
    if (isOtherEvents) {
      configArray?.forEach((element: configEvents) => {
        if (element.key === '0') {
          if (selectedEvent?.includes('panic-button')) {
            element.checked = true;
          }
        }
      });
    } else {
      configArray?.forEach((element: configEvents) => {
        if (selectedEvent?.includes(element.key)) {
          element.checked = true;
        }
      });
    }
  }
  private updateIncidentsTexts() {
    const isKm = this.currentMetricUnit === 'Kilometers';
    this.incidentsPerUnitLabel = isKm ? 'IncidentsPer100Km' : 'IncidentsPer100Miles';
    this.incidentsPerUnitDesc = isKm ? 'IncidentsPer100KmDesc' : 'IncidentsPer100MilesDesc';
  }

  public openFaq() {
    if (this.enableSelfCoaching) {
      this.dataService?.openFaq('SelfCoachingThresholds');
    } else {
      this.dataService?.openFaq('CoachingThresholds');
    }
  }
}
